<template>
  <div class="ModularProfessionPage flex flex-col gap-4">
    <component
      :is="mod.component"
      v-for="mod in resolvedModules"
      :key="`${mod.slug}-${mod.position}`"
      :module-config="mod.moduleConfig"
      :is-after-hero="mod.isAfterHero"
      :merged-heading-content="mod.mergedHeadingContent"
    />
  </div>
</template>

<script>
import { computed, toRefs } from "@nuxtjs/composition-api";
import useProfessionPageModules from "~/composables/useProfessionPageModules";
import {
  getBaseSlug,
  FULL_BLEED_HERO_SLUGS,
} from "~/config/professionPageModules";

export default {
  name: "ModularProfessionPage",
  props: {
    profession: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { profession } = toRefs(props);
    const { visibleModules, getComponent } = useProfessionPageModules({
      professionRef: profession,
    });

    const componentCache = {};
    const getCached = (slug) => {
      const base = getBaseSlug(slug);
      if (!componentCache[base]) {
        componentCache[base] = getComponent(slug);
      }
      return componentCache[base];
    };

    const resolvedModules = computed(() => {
      const list = visibleModules.value;
      const out = [];
      let prevBaseSlug = null;

      for (let i = 0; i < list.length; i++) {
        const mod = list[i];
        const base = getBaseSlug(mod.slug);
        const next = list[i + 1];
        const nextBase = next ? getBaseSlug(next.slug) : null;

        // Pair: section-heading immediately followed by people-swiper —
        // suppress the heading and attach its content to the swiper card.
        if (
          base === "profession-section-heading" &&
          nextBase === "profession-people-swiper"
        ) {
          out.push({
            slug: next.slug,
            position: next.position,
            component: getCached(next.slug),
            moduleConfig: next,
            isAfterHero: FULL_BLEED_HERO_SLUGS.has(prevBaseSlug),
            mergedHeadingContent: mod.content || null,
          });
          i++; // skip the swiper on the next iteration
          prevBaseSlug = "profession-people-swiper";
          continue;
        }

        out.push({
          slug: mod.slug,
          position: mod.position,
          component: getCached(mod.slug),
          moduleConfig: mod,
          isAfterHero:
            !FULL_BLEED_HERO_SLUGS.has(base) &&
            FULL_BLEED_HERO_SLUGS.has(prevBaseSlug),
          mergedHeadingContent: null,
        });
        prevBaseSlug = base;
      }

      return out;
    });

    return { resolvedModules };
  },
};
</script>
